<template>
<el-popover
  ref='popper'
  popper-class='hs-tree-select__popover'
  v-model='focused'
  trigger='manual'
  placement='bottom-start'
  :width='dropdownWidth'
>
  <el-tree
    ref='tree'
    style='height: 220px; overflow: auto'
    check-on-click-node
    :lazy='lazy'
    :load='load'
    :props='treeProps'
    :data='data'
    :expand-on-click-node='false'
    :node-key='treeProps.id'
    show-checkbox
    :check-strictly='!multiple || checkStrictly'
    :filter-node-method='filterNodeMethod'
    :default-checked-keys="checkedIds"
    @mouseup.native.stop=''
    @check='handleCheck'
  >
    <template slot-scope="{ node, data }">
      <span class='tree-node-label' :class='{ "is-disabled": data.disabled }'> {{ data[node.store.props.label] }} </span>
    </template>
  </el-tree>
  <div class='select-all-wrap' v-if='multiple && showSelectAll' @mouseup.stop=''>
    <el-checkbox
      :indeterminate="indeterminate"
      :value="checkAll"
      @change="handleCheckAllChange"
    >{{ t('select all') }}</el-checkbox>
  </div>

  <div
    ref='ref'
    class='hs-tree-select'
    :class='{ "is-focused": focused, "is-disabled": disabled, "clearable": clearable }'
    slot='reference'
    @mousedown='toggle'
    v-clickoutside='blur'
  >
    <template v-if='multiple'>
      <template v-if='collapseTags'>
        <el-tag
          closable
          class='hs-tree-select__tag'
          v-if='checkeds.length > 0'
          size="small"
          :class='{ "is-disabled": disabled || checkeds[0].disabled }'
          type="info"
          @mousedown.native.stop=''
          @close='removeChecked(0)'
        > {{ checkeds[0][treeProps.label] }} </el-tag>
        <el-tag
          class='hs-tree-select__tag'
          v-if='checkeds.length > 1'
          type="info"
          @mousedown.native.stop=''
          size="small"
        > +{{ checkeds.length - 1 }} </el-tag>
      </template>
      <template v-else>
        <el-tag
          closable
          v-for='(checked, i) in checkeds'
          :key='checked[treeProps.id]'
          class='hs-tree-select__tag'
          size="small"
          :class='{ "is-disabled": disabled || checked.disabled }'
          type="info"
          @mousedown.native.stop=''
          @close='removeChecked(i)'
        > {{ checked[treeProps.label] }} </el-tag>
      </template>
    </template>

    <input
      ref='input'
      class='hs-tree-select-input'
      v-model="keyword"
      :placeholder='placeholder'
    >
    <input
      ref='hiddenInput'
      class='hs-tree-select-input is-hidden'
      :value="placeholder || keyword"
    >

    <span
      class='hs-tree-select__icon-caret'
      @mousedown.stop
      @click='!disabled && clearable ? handleCheckAllChange(false) : toggle()'
      @mouseenter='!disabled && clearable && (caretIcon = "el-icon-close")'
      @mouseleave='!disabled && clearable && (caretIcon = "el-icon-arrow-down")'
    >
      <i :class='caretIcon'></i>
    </span>
  </div>
</el-popover>
</template>

<script>
import Clickoutside from 'element-ui/src/utils/clickoutside';
import Locale from '../../mixins/locale'
import _ from 'lodash'

const props = {
  id: 'id',
  label: 'label',
  children: 'children'
}

const flatten = (tree, props) => {
  const nodeMap = {};
  const childrenKey = props.children;
  const idKey = props.id
  const iterator = (nodes, parentPath = []) => {
    nodes.forEach(node => {
      const id = node[idKey]
      const path = [...parentPath, id]
      node.keyPath = path
      node[childrenKey] && iterator(node[childrenKey], path);
      nodeMap[id] = node;
    })
  }
  iterator(tree);

  return nodeMap;
}

export default {
  name: 'HsTreeSelect',
  mixins: [Locale],
  directives: { Clickoutside },
  props: {
    /**
     * 树节点加载方法
     */
    load: {
      type: Function,
      default (node, resolve) { resolve([]) }
    },
    /**
     * 树数据
     */
    data: {
      type: Array,
      default () { return [] }
    },
    /**
     * ElTree bind的 属性配置对象
     */
    props: {
      type: Object,
      default () { return props }
    },
    /**
     * 是否是懒加载
     */
    lazy: {
      type: Boolean,
      default: false
    },
    /**
     * 是否禁用属性选择器
     */
    disabled: {
      type: Boolean,
      default: false
    },
    /**
     * 是否支持多选
     */
    multiple: {
      type: Boolean,
      default: true
    },
    /**
     * 是否显示全选复选框
     */
    showSelectAll: {
      type: Boolean,
      default: false
    },
    /**
     * 树节点是否按照父子不相关联的策略
     */
    checkStrictly: {
      type: Boolean,
      default: false
    },
    /**
     * 当选中数量超过一个时，是否折叠其余的 tag
     */
    collapseTags: {
      type: Boolean,
      default: false
    },
    /**
     * 树节点过滤方法
     */
    filterNodeMethod: {
      type: Function,
      default (value, data) {
        return data.label.includes(value) || (data.id + '').includes(value);
      }
    },
    /**
     * 默认选中
     */
    value: [Array, String, Number],
    /**
     * 获取树选中节点策略
     * 
     * 当 checkStrictly 为 false 时生效
     * 可选值：root、leaf、all；
     * 
     * root：返回被选中的根节点；
     * leaf：返回所有选中的叶子节点；
     * all：返回所有被选中的节点；
     */
    valueStrategy: {
      type: String,
      validator: _v => ['root', 'leaf', 'all'].includes(_v),
      default: 'all'
    },
    /**
     * 是否显示清除按钮
     */
    clearable: {
      type: Boolean,
      default: false,
    }
  },
  data () {
    return {
      keyword: '',
      checkeds: [],
      checkedIds: [],
      checkAll: false,
      indeterminate: false,
      focused: false,
      dropdownWidth: 0,
      placeholder: '',
      treeProps: {
        ...props,
        ...this.props
      },
      nodeMap: {},
      nodeLength: 0,
      caretIcon: 'el-icon-arrow-down'
    }
  },
  computed: {
    _valueStretegy () {
      return !this.multiple || this.checkStrictly ? 'all' : this.valueStrategy
    }
  },

  mounted () {
    this.init();
  },

  methods: {
    focus () {
      if (this.disabled) return
      this.focused = true
      this.dropdownWidth = this.$refs.ref.clientWidth;
      setTimeout(() => this.$refs.input.focus());
      this.$refs.popper.updatePopper()
    },
    blur () {
      this.focused = false
    },
    toggle () {
      this.focused ? this.blur() : this.focus()
    },
    init () {
      this.keyword = this.placeholder = ''
      const nodeMap = this.nodeMap = flatten(this.data, this.treeProps);
      this.nodeLength = Object.keys(this.nodeMap).length;
      const value = this.multiple ? this.value || [] : [this.value];
      const checkeds = [];
      value.forEach(id => nodeMap[id] && checkeds.push(nodeMap[id]));
      this.checkeds = checkeds
      this.handleChange(checkeds)
    },

    // 勾选树节点时的事件处理
    handleCheck (data, tree) {
      let checkeds = []
      if (!this.multiple) {
        checkeds = tree.checkedNodes.length ? [data] : [];
        this.focused = false;
      } else {
        checkeds = this.getCheckedByValueStretegy()
      }
      
      this.handleChange(checkeds)
    },

    // 点击全选按钮时的事件处理
    handleCheckAllChange (v) {
      let checkedKeys = []
      if (v) {
        const allIds = Object.keys(this.nodeMap)
        const [enabledIds, disabledIds] = allIds.reduce((res, id) => {
          res[this.nodeMap[id].disabled ? 1 : 0].push(id)
          return res
        }, [[], []])

        // 移除禁用节点的父元素id
        const ids = enabledIds.filter(id => {
          return disabledIds.every(_id => !this.isParentNode(this.nodeMap[id], this.nodeMap[_id]))
        })
        checkedKeys = Array.from(new Set([...ids, ...this.checkeds.map(n => n[this.treeProps.id])]))
      } else {
        checkedKeys = this.checkeds.reduce((res, n) => {
          if (n.disabled) res.push(n[this.treeProps.id])
          return res
        }, [])
      }
      
      this.$refs.tree.setCheckedKeys(checkedKeys)
      this.handleChange(this.getCheckedByValueStretegy())
    },

    getCheckedByValueStretegy () {
      const vs = this._valueStretegy;
      if (vs === 'root') {
        const allCheckeds = this.$refs.tree.getCheckedNodes()
        return allCheckeds.reduce((res, n) => {
          if (allCheckeds.some(_n => this.isParentNode(_n, n))) return res
          res.push(n)
          return res
        }, [])
      } else {
        return this.$refs.tree.getCheckedNodes(vs === 'leaf')
      }

    },
    isParentNode(parent, node) {
      const parentPath = parent.keyPath
      const path = node.keyPath

      if (path.length <= parentPath.length) return false

      for (let i = 0; i < parentPath.length; i++) {
        if (parentPath[i] !== path[i]) return false
      }
      return true
    },

    removeChecked (i) {
      const node = this.checkeds[i]
      if (this.disabled || node.disabled) return
      let checkeds = [...this.checkeds]
      checkeds.splice(i, 1)
      
      if (!this.checkStrictly) {
        checkeds = checkeds.filter(n => !this.isParentNode(n, node) && !this.isParentNode(node, n))
      }
      
      this.handleChange(checkeds)
    },
    
    handleChange (checkeds) {
      const oldCheckedIds = this.checkeds.map(n => n[this.treeProps.id]);
      const checkedIds = checkeds.map(n => n[this.treeProps.id]);

      // 通知变化
      if (oldCheckedIds.length !== checkedIds.length || _.difference(oldCheckedIds, checkedIds).length !== 0) {
        const value = this.multiple ? checkedIds : checkedIds[0] || null
        this.checkeds = checkeds
        this.$emit('input', value);
        this.$emit('change', value);
        this.dispatch('ElFormItem', 'el.form.change', value)
      }

      this.$nextTick(() => {
        // 设置单选时的输入框显示内容
        if (!this.multiple) {
          const val = this.checkeds[0] && this.checkeds[0][this.treeProps.label] || '';
          this.placeholder = val;
          if (this.keyword) {
            this.keyword = '';
          }
          this.$nextTick(() => {
            this.$refs.input.style.width = this.$refs.hiddenInput.scrollWidth + 'px';
          })
        }

        // 更新树选择状态
        this.$refs.tree.setCheckedNodes(this.checkeds);
        
        // 设置全选复选框状态
        const checkedLength = this.$refs.tree.getCheckedNodes().length
        this.checkAll = checkedLength === this.nodeLength
        this.indeterminate = checkedLength > 0 && checkedLength < this.nodeLength
        
        // 更新弹窗位置
        this.$refs.popper.updatePopper()
      })
    },

    // 触发组件名为 ${componentName} 的上级元素的 ${eventName} 事件，并传递参数
    dispatch (componentName, eventName, params) {
      var parent = this.$parent || this.$root;
      var name = parent.$options.componentName;

      while (parent && (!name || name !== componentName)) {
        parent = parent.$parent;

        if (parent) {
          name = parent.$options.componentName;
        }
      }
      if (parent) {
        parent.$emit.apply(parent, [eventName].concat(params));
      }
    },
  },

  watch: {
    data () {
      this.init()
    },
    keyword () {
      this.$refs.tree.filter(this.keyword);
      setTimeout(() => {
        this.$refs.input.style.width = this.$refs.hiddenInput.scrollWidth + 'px';
      })
    },
    value () {
      this.init()
    }
  }
}
</script>

<style scoped lang='scss'>
@import '../../theme/var.scss';
@import '../../theme/element.scss';
.hs-tree-select {
  position: relative;
  min-width: 172px;
  padding: 0px 24px 0 3px;
  min-height:$--input-height;
  line-height: $--input-height;
  box-sizing: border-box;
  border: 1px solid #ddd;
  background: $--color-white;
  border-radius: $--border-radius-base;
  &.is-focused {
    border: 1px solid $--color-primary;
  }
  &::after {
    content: '';
    display: block;
    clear: both;
  }
  &__tag {
    float: left;
    margin: 2px 0 2px 6px;
    // margin: 0 4px 2px 0;
  }
  &-input {
    float: left;
    width: 1px;
    max-width: calc(100% - 8px);
    margin: 2px 0;
    height: 24px;
    border: none;
    outline: none;
    background: transparent;
    font-size: $--font-size-base;
    pointer-events: none;
    &.is-hidden {
      position: fixed;
      opacity: 0;
      pointer-events: none;
      width: 0;
    }
  }

  &__icon-caret {
    position: absolute;
    top: 0;
    right: 4px;
    bottom: 0;
    width: 20px;
    color: #999;
    font-size: 12px;
    [class^='el-icon'] {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }
  &.is-focused &__icon-caret [class^='el-icon'] {
    transform: translate(-50%, -50%) rotate(180deg);
  }
  &.is-disabled {
    background-color: #f5f6f7;
    border-color: #E4E7ED;
    color: #C0C4CC;
    cursor: not-allowed;
    .hs-tree-select-input::-webkit-input-placeholder {
      color: #C0C4CC;
    }
  }
}
.select-all-wrap {
  padding: 10px 10px 4px;
  margin-top: 10px;
  border-top: 1px solid #ddd;
}
.tree-node-label.is-disabled {
  color: #C0C4CC;
}
</style>
<style lang='scss'>
.hs-tree-select__popover {
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  background-color: #fff;
  padding: 6px 0;
}
.hs-tree-select {
  .el-tree-node__label{
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .hs-tree-select__tag.is-disabled {
    background-color: #e4e7ed;
    border-color: #e4e7ed;
    color: #999;
    .el-icon-close {
      cursor: not-allowed;
      &:hover {
        color: #999;
      }
    }
  }
}
</style>